import React, { useState, useEffect } from 'react';
import { providersAPI } from '../../api/providers';
import { bookingsAPI } from '../../api/bookings';
import Spinner from '../../components/common/Spinner';
import {
  CalendarDaysIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  StarIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
} from '@heroicons/react/24/outline';
import { formatCurrency } from '../../utils/formatter';

const ProviderAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [provider, setProvider] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [clientPage, setClientPage] = useState(1);
  const [clientFilter, setClientFilter] = useState('all');
  const [clientSearch, setClientSearch] = useState('');
  const clientsPerPage = 5;

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const profile = await providersAPI.getMyProfile();
      setProvider(profile);

      try {
        const data = await bookingsAPI.getBookings({ providerId: profile.id }, { silent: true });
        setBookings(Array.isArray(data) ? data : data?.bookings || []);
      } catch { /* bookings service may not be accessible */ }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="large" />
      </div>
    );
  }

  const services = provider?.services || [];
  const reviews = provider?.reviews || [];
  const rating = provider?.rating || 0;
  const totalReviews = provider?.totalReviews || 0;

  // Computed metrics from actual bookings data
  const totalBookings = bookings.length;
  const completedBookings = bookings.filter(b => b.status === 'COMPLETED').length;
  const cancelledBookings = bookings.filter(b => b.status === 'CANCELLED' || b.status === 'DECLINED').length;
  const pendingBookings = bookings.filter(b => b.status === 'PENDING').length;
  const confirmedBookings = bookings.filter(b => b.status === 'CONFIRMED').length;
  const completionRate = totalBookings > 0 ? Math.round((completedBookings / totalBookings) * 100) : 0;
  const avgResponseTime = provider?.stats?.avgResponseTime;

  // Revenue from completed bookings
  const totalRevenue = bookings
    .filter(b => b.status === 'COMPLETED')
    .reduce((sum, b) => sum + ((b.subtotal || 0) - (b.commission || 0)), 0);

  // Service breakdown
  // Map serviceId to service name from provider's services
  const serviceNameMap = {};
  services.forEach(s => { serviceNameMap[s.id] = s.name; });

  const serviceBookingCounts = {};
  bookings.forEach(b => {
    const name = b.service?.name || serviceNameMap[b.serviceId] || 'Unknown';
    serviceBookingCounts[name] = (serviceBookingCounts[name] || 0) + 1;
  });
  const topServices = Object.entries(serviceBookingCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  // Monthly trend (last 6 months)
  const monthlyData = {};
  const now = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en', { month: 'short' });
    monthlyData[key] = { label, bookings: 0, revenue: 0, completed: 0, pending: 0, confirmed: 0, cancelled: 0 };
  }
  bookings.forEach(b => {
    const d = new Date(b.createdAt);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (monthlyData[key]) {
      monthlyData[key].bookings++;
      if (b.status === 'COMPLETED') {
        monthlyData[key].completed++;
        monthlyData[key].revenue += (b.subtotal || 0) - (b.commission || 0);
      } else if (b.status === 'PENDING') {
        monthlyData[key].pending++;
      } else if (b.status === 'CONFIRMED') {
        monthlyData[key].confirmed++;
      } else if (b.status === 'CANCELLED' || b.status === 'DECLINED') {
        monthlyData[key].cancelled++;
      }
    }
  });
  const months = Object.values(monthlyData);
  const maxBookings = Math.max(...months.map(m => m.bookings), 1);
  const maxRevenue = Math.max(...months.map(m => m.revenue), 1);

  // Review breakdown
  const ratingCounts = [0, 0, 0, 0, 0];
  reviews.forEach(r => {
    if (r.rating >= 1 && r.rating <= 5) ratingCounts[r.rating - 1]++;
  });

  const StatCard = ({ icon: Icon, label, value, sub, color }) => (
    <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--gray-2)', border: '1px solid var(--gray-4)' }}>
      <div className="flex items-center gap-3 mb-3">
        <div style={{ width: '36px', height: '36px', borderRadius: '10px', backgroundColor: color || 'var(--orange-3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon style={{ width: '18px', height: '18px', color: 'var(--orange-9)' }} />
        </div>
        <span style={{ fontSize: '13px', color: 'var(--gray-10)', fontWeight: 500 }}>{label}</span>
      </div>
      <p style={{ fontSize: '28px', fontWeight: 700, color: 'var(--gray-12)', margin: 0, textAlign: 'left', fontFamily: "'Poppins', sans-serif" }}>
        {value}
      </p>
      {sub && <p style={{ fontSize: '12px', color: 'var(--gray-10)', margin: '4px 0 0 0', textAlign: 'left' }}>{sub}</p>}
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-xl sm:text-2xl mb-2" style={{ color: 'var(--gray-12)' }}>Analytics</h1>
          <p style={{ color: 'var(--gray-11)' }}>Track your business performance and growth</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={CalendarDaysIcon} label="Total Bookings" value={totalBookings} sub={`${pendingBookings} pending`} />
          <StatCard icon={CheckCircleIcon} label="Completed" value={completedBookings} sub={`${completionRate}% completion rate`} />
          <StatCard icon={CurrencyDollarIcon} label="Revenue" value={formatCurrency(totalRevenue)} sub="After commission" />
          <StatCard icon={StarIcon} label="Rating" value={rating.toFixed(1)} sub={`${totalReviews} review${totalReviews !== 1 ? 's' : ''}`} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Booking Trend Chart — Stacked by status */}
          <div className="rounded-xl p-6" style={{ backgroundColor: 'var(--gray-2)', border: '1px solid var(--gray-4)' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--gray-12)', margin: 0, textAlign: 'left' }}>
                Booking Trend
              </h3>
              <div className="flex items-center gap-4">
                {[
                  { label: 'Completed', color: '#22c55e' },
                  { label: 'Confirmed', color: '#3b82f6' },
                  { label: 'Pending', color: 'var(--orange-9)' },
                  { label: 'Cancelled', color: 'var(--red-9, #e5484d)' },
                ].map(l => (
                  <div key={l.label} className="flex items-center gap-1">
                    <div style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: l.color }} />
                    <span style={{ fontSize: '11px', color: 'var(--gray-10)' }}>{l.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: '160px' }}>
              {months.map((m, i) => {
                const total = m.bookings;
                const barHeight = Math.max((total / maxBookings) * 130, total > 0 ? 8 : 4);
                const segments = [
                  { count: m.completed, color: '#22c55e', label: 'Completed' },
                  { count: m.confirmed, color: '#3b82f6', label: 'Confirmed' },
                  { count: m.pending, color: 'var(--orange-9)', label: 'Pending' },
                  { count: m.cancelled, color: 'var(--red-9, #e5484d)', label: 'Cancelled' },
                ].filter(s => s.count > 0);

                return (
                  <div
                    key={i}
                    style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', height: '100%', justifyContent: 'flex-end', position: 'relative' }}
                    className="group"
                  >
                    {/* Tooltip on hover */}
                    <div
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{
                        position: 'absolute', bottom: `${barHeight + 24}px`, left: '50%', transform: 'translateX(-50%)',
                        backgroundColor: 'var(--gray-12)', color: 'var(--gray-1)', padding: '6px 10px',
                        borderRadius: '6px', fontSize: '11px', whiteSpace: 'nowrap', zIndex: 10,
                        pointerEvents: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                      }}
                    >
                      <div style={{ fontWeight: 600, marginBottom: '2px' }}>{m.label}: {total} booking{total !== 1 ? 's' : ''}</div>
                      {segments.map(s => (
                        <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <div style={{ width: '6px', height: '6px', borderRadius: '1px', backgroundColor: s.color }} />
                          {s.label}: {s.count}
                        </div>
                      ))}
                    </div>

                    {/* Stacked bar */}
                    <div style={{
                      width: '100%', maxWidth: '40px', height: `${barHeight}px`,
                      borderRadius: '4px 4px 0 0', overflow: 'hidden',
                      display: 'flex', flexDirection: 'column-reverse',
                      transition: 'height 0.3s ease', cursor: 'pointer',
                    }}>
                      {total === 0 ? (
                        <div style={{ width: '100%', height: '100%', backgroundColor: 'var(--gray-4)' }} />
                      ) : (
                        segments.map(s => (
                          <div
                            key={s.label}
                            style={{
                              width: '100%',
                              height: `${(s.count / total) * 100}%`,
                              backgroundColor: s.color,
                              minHeight: '2px',
                            }}
                          />
                        ))
                      )}
                    </div>
                    <span style={{ fontSize: '11px', color: 'var(--gray-10)' }}>{m.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Revenue Trend Chart */}
          <div className="rounded-xl p-6" style={{ backgroundColor: 'var(--gray-2)', border: '1px solid var(--gray-4)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--gray-12)', marginBottom: '16px', textAlign: 'left' }}>
              Revenue Trend
            </h3>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: '160px' }}>
              {months.map((m, i) => (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', height: '100%', justifyContent: 'flex-end' }}>
                  <span style={{ fontSize: '10px', fontWeight: 600, color: 'var(--gray-11)' }}>
                    {m.revenue > 0 ? formatCurrency(m.revenue).replace('USh ', '') : ''}
                  </span>
                  <div
                    style={{
                      width: '100%',
                      maxWidth: '40px',
                      height: `${Math.max((m.revenue / maxRevenue) * 120, 4)}px`,
                      backgroundColor: 'var(--green-9, #22c55e)',
                      borderRadius: '4px 4px 0 0',
                      transition: 'height 0.3s ease',
                    }}
                  />
                  <span style={{ fontSize: '11px', color: 'var(--gray-10)' }}>{m.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Performance Metrics */}
          <div className="rounded-xl p-6" style={{ backgroundColor: 'var(--gray-2)', border: '1px solid var(--gray-4)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--gray-12)', marginBottom: '16px', textAlign: 'left' }}>
              Performance
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {/* Completion Rate */}
              <div>
                <div className="flex justify-between mb-1">
                  <span style={{ fontSize: '13px', color: 'var(--gray-11)' }}>Completion Rate</span>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gray-12)' }}>{completionRate}%</span>
                </div>
                <div style={{ height: '6px', borderRadius: '3px', backgroundColor: 'var(--gray-4)' }}>
                  <div style={{ height: '100%', width: `${completionRate}%`, borderRadius: '3px', backgroundColor: 'var(--orange-9)', transition: 'width 0.5s ease' }} />
                </div>
              </div>

              {/* Cancellation Rate */}
              <div>
                <div className="flex justify-between mb-1">
                  <span style={{ fontSize: '13px', color: 'var(--gray-11)' }}>Cancellation Rate</span>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gray-12)' }}>
                    {totalBookings > 0 ? Math.round((cancelledBookings / totalBookings) * 100) : 0}%
                  </span>
                </div>
                <div style={{ height: '6px', borderRadius: '3px', backgroundColor: 'var(--gray-4)' }}>
                  <div style={{
                    height: '100%',
                    width: `${totalBookings > 0 ? (cancelledBookings / totalBookings) * 100 : 0}%`,
                    borderRadius: '3px', backgroundColor: 'var(--red-9, #e5484d)',
                  }} />
                </div>
              </div>

              {/* Response Time */}
              <div className="flex justify-between">
                <span style={{ fontSize: '13px', color: 'var(--gray-11)' }}>Avg Response Time</span>
                <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gray-12)' }}>
                  {avgResponseTime ? `${avgResponseTime} min` : 'N/A'}
                </span>
              </div>

              {/* Active Services */}
              <div className="flex justify-between">
                <span style={{ fontSize: '13px', color: 'var(--gray-11)' }}>Active Services</span>
                <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gray-12)' }}>{services.length}</span>
              </div>

              {/* Last Booking */}
              <div className="flex justify-between">
                <span style={{ fontSize: '13px', color: 'var(--gray-11)' }}>Last Booking</span>
                <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gray-12)' }}>
                  {bookings.length > 0 ? new Date(bookings[0].createdAt).toLocaleDateString() : 'None yet'}
                </span>
              </div>
            </div>
          </div>

          {/* Top Services */}
          <div className="rounded-xl p-6" style={{ backgroundColor: 'var(--gray-2)', border: '1px solid var(--gray-4)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--gray-12)', marginBottom: '16px', textAlign: 'left' }}>
              Top Services
            </h3>
            {topServices.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {topServices.map(([name, count], i) => (
                  <div key={name} className="flex items-center gap-3">
                    <span style={{
                      width: '24px', height: '24px', borderRadius: '6px', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700,
                      backgroundColor: i === 0 ? 'var(--orange-9)' : 'var(--gray-4)',
                      color: i === 0 ? '#fff' : 'var(--gray-11)',
                    }}>
                      {i + 1}
                    </span>
                    <div className="flex-1">
                      <p style={{ fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)', margin: 0, textAlign: 'left' }}>{name}</p>
                    </div>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--orange-9)' }}>
                      {count} booking{count !== 1 ? 's' : ''}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ fontSize: '14px', color: 'var(--gray-9)', textAlign: 'center', padding: '20px 0' }}>
                No booking data yet
              </p>
            )}
          </div>

          {/* Rating Breakdown */}
          <div className="rounded-xl p-6" style={{ backgroundColor: 'var(--gray-2)', border: '1px solid var(--gray-4)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--gray-12)', marginBottom: '4px', textAlign: 'left' }}>
              Reviews
            </h3>
            <div className="flex items-center gap-2 mb-4">
              <span style={{ fontSize: '32px', fontWeight: 700, color: 'var(--gray-12)', fontFamily: "'Poppins', sans-serif" }}>
                {rating.toFixed(1)}
              </span>
              <div>
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map(s => (
                    <StarIcon
                      key={s}
                      style={{
                        width: '16px', height: '16px',
                        color: s <= Math.round(rating) ? '#facc15' : 'var(--gray-6)',
                        fill: s <= Math.round(rating) ? '#facc15' : 'none',
                      }}
                    />
                  ))}
                </div>
                <p style={{ fontSize: '12px', color: 'var(--gray-10)', margin: '2px 0 0 0', textAlign: 'left' }}>
                  {totalReviews} review{totalReviews !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {[5, 4, 3, 2, 1].map(star => {
                const count = ratingCounts[star - 1];
                const pct = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
                return (
                  <div key={star} className="flex items-center gap-2">
                    <span style={{ fontSize: '12px', color: 'var(--gray-11)', width: '12px' }}>{star}</span>
                    <StarIcon style={{ width: '12px', height: '12px', color: '#facc15', fill: '#facc15' }} />
                    <div style={{ flex: 1, height: '6px', borderRadius: '3px', backgroundColor: 'var(--gray-4)' }}>
                      <div style={{ height: '100%', width: `${pct}%`, borderRadius: '3px', backgroundColor: '#facc15' }} />
                    </div>
                    <span style={{ fontSize: '12px', color: 'var(--gray-10)', width: '24px', textAlign: 'right' }}>{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Top Clients Section */}
        {(() => {
          // Build client stats from bookings
          const clientMap = {};
          bookings.forEach(b => {
            const id = b.clientId;
            if (!clientMap[id]) {
              clientMap[id] = {
                id,
                name: b.client?.name || b.clientName || `Client-${id.slice(0, 6).toUpperCase()}`,
                totalBookings: 0,
                completed: 0,
                cancelled: 0,
                totalSpent: 0,
                lastBooking: null,
              };
            }
            clientMap[id].totalBookings++;
            if (b.status === 'COMPLETED') {
              clientMap[id].completed++;
              clientMap[id].totalSpent += (b.subtotal || 0);
            }
            if (b.status === 'CANCELLED' || b.status === 'DECLINED') clientMap[id].cancelled++;
            const bDate = new Date(b.createdAt);
            if (!clientMap[id].lastBooking || bDate > clientMap[id].lastBooking) clientMap[id].lastBooking = bDate;
          });

          let clients = Object.values(clientMap);

          // Filter
          if (clientFilter === 'repeat') clients = clients.filter(c => c.totalBookings > 1);
          if (clientFilter === 'top-spenders') clients = clients.sort((a, b) => b.totalSpent - a.totalSpent);
          else clients = clients.sort((a, b) => b.totalBookings - a.totalBookings);

          // Search
          if (clientSearch.trim()) {
            const q = clientSearch.toLowerCase();
            clients = clients.filter(c => c.name.toLowerCase().includes(q) || c.id.toLowerCase().includes(q));
          }

          const totalPages = Math.max(Math.ceil(clients.length / clientsPerPage), 1);
          const paginated = clients.slice((clientPage - 1) * clientsPerPage, clientPage * clientsPerPage);

          return (
            <div className="rounded-xl p-6 mt-6" style={{ backgroundColor: 'var(--gray-2)', border: '1px solid var(--gray-4)' }}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-5">
                <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--gray-12)', margin: 0, textAlign: 'left' }}>
                  Top Clients
                  <span style={{ fontSize: '12px', fontWeight: 400, color: 'var(--gray-9)', marginLeft: '8px' }}>
                    ({Object.keys(clientMap).length} total)
                  </span>
                </h3>
                <div className="flex items-center gap-3">
                  {/* Search */}
                  <input
                    type="text"
                    placeholder="Search clients..."
                    value={clientSearch}
                    onChange={e => { setClientSearch(e.target.value); setClientPage(1); }}
                    style={{
                      padding: '6px 12px', borderRadius: '6px', fontSize: '13px',
                      border: '1px solid var(--gray-6)', backgroundColor: 'var(--color-background)',
                      color: 'var(--gray-12)', outline: 'none', width: '180px',
                    }}
                  />
                  {/* Filter */}
                  <select
                    value={clientFilter}
                    onChange={e => { setClientFilter(e.target.value); setClientPage(1); }}
                    style={{
                      padding: '6px 10px', borderRadius: '6px', fontSize: '13px',
                      border: '1px solid var(--gray-6)', backgroundColor: 'var(--color-background)',
                      color: 'var(--gray-12)', cursor: 'pointer',
                    }}
                  >
                    <option value="all">All Clients</option>
                    <option value="repeat">Repeat Clients</option>
                    <option value="top-spenders">Top Spenders</option>
                  </select>
                </div>
              </div>

              {paginated.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'var(--gray-9)', fontSize: '14px', padding: '24px 0' }}>
                  {clientSearch ? 'No clients match your search' : 'No client data yet'}
                </p>
              ) : (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {paginated.map(client => (
                      <div
                        key={client.id}
                        className="flex items-center justify-between p-4 rounded-lg"
                        style={{ backgroundColor: 'var(--color-background)', border: '1px solid var(--gray-4)' }}
                      >
                        {/* Left: avatar + name */}
                        <div className="flex items-center gap-3">
                          <div style={{
                            width: '40px', height: '40px', borderRadius: '50%',
                            backgroundColor: 'var(--orange-3)', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            fontSize: '15px', fontWeight: 700, color: 'var(--orange-9)', flexShrink: 0,
                          }}>
                            {client.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--gray-12)', margin: 0, textAlign: 'left' }}>
                              {client.name}
                            </p>
                            <p style={{ fontSize: '12px', color: 'var(--gray-10)', margin: 0, textAlign: 'left' }}>
                              Last visit: {client.lastBooking ? client.lastBooking.toLocaleDateString() : 'N/A'}
                            </p>
                          </div>
                        </div>

                        {/* Right: stats */}
                        <div className="flex items-center gap-6">
                          <div style={{ textAlign: 'center' }}>
                            <p style={{ fontSize: '18px', fontWeight: 700, color: 'var(--gray-12)', margin: 0, fontFamily: "'Poppins', sans-serif" }}>
                              {client.totalBookings}
                            </p>
                            <p style={{ fontSize: '11px', color: 'var(--gray-9)', margin: 0 }}>Bookings</p>
                          </div>
                          <div style={{ width: '1px', height: '30px', backgroundColor: 'var(--gray-4)' }} />
                          <div style={{ textAlign: 'center' }}>
                            <p style={{ fontSize: '18px', fontWeight: 700, color: '#22c55e', margin: 0, fontFamily: "'Poppins', sans-serif" }}>
                              {client.completed}
                            </p>
                            <p style={{ fontSize: '11px', color: 'var(--gray-9)', margin: 0 }}>Completed</p>
                          </div>
                          <div style={{ width: '1px', height: '30px', backgroundColor: 'var(--gray-4)' }} />
                          <div style={{ textAlign: 'center' }}>
                            <p style={{ fontSize: '16px', fontWeight: 700, color: 'var(--orange-9)', margin: 0, fontFamily: "'Poppins', sans-serif" }}>
                              {formatCurrency(client.totalSpent)}
                            </p>
                            <p style={{ fontSize: '11px', color: 'var(--gray-9)', margin: 0 }}>Spent</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4 pt-3" style={{ borderTop: '1px solid var(--gray-4)' }}>
                      <span style={{ fontSize: '12px', color: 'var(--gray-10)' }}>
                        {(clientPage - 1) * clientsPerPage + 1}–{Math.min(clientPage * clientsPerPage, clients.length)} of {clients.length}
                      </span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => setClientPage(p => Math.max(1, p - 1))}
                          disabled={clientPage === 1}
                          style={{
                            padding: '4px 10px', borderRadius: '4px', border: 'none', fontSize: '13px',
                            cursor: clientPage === 1 ? 'default' : 'pointer',
                            backgroundColor: 'var(--gray-4)', color: 'var(--gray-11)',
                            opacity: clientPage === 1 ? 0.4 : 1,
                          }}
                        >
                          Prev
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                          <button
                            key={page}
                            onClick={() => setClientPage(page)}
                            style={{
                              width: '28px', height: '28px', borderRadius: '4px', border: 'none',
                              fontSize: '12px', fontWeight: clientPage === page ? 600 : 400, cursor: 'pointer',
                              backgroundColor: clientPage === page ? 'var(--orange-9)' : 'var(--gray-4)',
                              color: clientPage === page ? '#fff' : 'var(--gray-11)',
                            }}
                          >
                            {page}
                          </button>
                        ))}
                        <button
                          onClick={() => setClientPage(p => Math.min(totalPages, p + 1))}
                          disabled={clientPage === totalPages}
                          style={{
                            padding: '4px 10px', borderRadius: '4px', border: 'none', fontSize: '13px',
                            cursor: clientPage === totalPages ? 'default' : 'pointer',
                            backgroundColor: 'var(--gray-4)', color: 'var(--gray-11)',
                            opacity: clientPage === totalPages ? 0.4 : 1,
                          }}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })()}

      </div>
    </div>
  );
};

export default ProviderAnalytics;
